import React, { useEffect, useState } from 'react';
import { Phone, Car, User as UserIcon, Clock, UserPlus, Handshake, Instagram, Facebook, Globe, Footprints, Users as UsersIcon, MessageCircle, Pencil } from 'lucide-react';
import { EditLeadCustomerDialog } from './EditLeadCustomerDialog';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export const STATUS_META = {
  NEW: { label: 'Novo', cls: 'text-sky-400 bg-sky-400/10' },
  BDC_ASSIGNED: { label: 'Atribuído', cls: 'text-primary bg-primary/15' },
  CONTACTING: { label: 'Em contato', cls: 'text-teal-400 bg-teal-400/10' },
  FOLLOW_UP: { label: 'Follow-Up', cls: 'text-amber-400 bg-amber-400/10' },
  APPOINTMENT_SCHEDULED: { label: 'Agendado', cls: 'text-violet-400 bg-violet-400/10' },
  SALESPERSON_REQUESTED: { label: 'Vendedor solicitado', cls: 'text-orange-400 bg-orange-400/10' },
  AWAITING_SALES_ASSIGNMENT: { label: 'Aguardando vendedor', cls: 'text-pink-400 bg-pink-400/10' },
  ASSIGNED_TO_SALESPERSON: { label: 'Com vendedor', cls: 'text-emerald-400 bg-emerald-400/10' },
  LOST: { label: 'Perdido', cls: 'text-muted-foreground bg-muted/40' },
};

export const SOURCES = ['CarGurus', 'Capital One', 'Facebook', 'Instagram', 'Google', 'Website', 'Walk-In', 'Phone', 'Referral', 'Landing Page', 'Other'];

export const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: 'text-muted-foreground bg-muted/40' };
  return <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full font-semibold whitespace-nowrap ${m.cls}`}><span className="w-1.5 h-1.5 rounded-full bg-current neon-dot" />{m.label}</span>;
};

export const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
};

export const leadName = (l) => `${l.first_name || ''} ${l.last_name || ''}`.trim() || 'Sem nome';

export const isWalkIn = (l) => (l?.source || '').toLowerCase().replace(/[^a-z]/g, '').includes('walkin');

const SOURCE_ICONS = [
  ['instagram', Instagram, 'text-pink-400'],
  ['facebook', Facebook, 'text-sky-400'],
  ['meta', Facebook, 'text-sky-400'],
  ['whatsapp', MessageCircle, 'text-emerald-400'],
  ['walk', Footprints, 'text-amber-400'],
  ['phone', Phone, 'text-teal-400'],
  ['referral', UsersIcon, 'text-violet-400'],
  ['cargurus', Car, 'text-primary'],
  ['capital one', Car, 'text-primary'],
];

export const sourceVisual = (source) => {
  const s = (source || '').toLowerCase();
  const hit = SOURCE_ICONS.find(([term]) => s.includes(term));
  return { Icon: hit ? hit[1] : Globe, cls: hit ? hit[2] : 'text-muted-foreground/60' };
};

export const SourceIcon = ({ source, className = 'w-3.5 h-3.5', testid }) => {
  const { Icon, cls } = sourceVisual(source);
  return <Icon className={`${className} ${cls} shrink-0`} title={source || 'Origem não informada'} data-testid={testid} />;
};

const inputCls = 'w-full bg-muted/40 border border-border rounded-lg px-3.5 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

export const AssignAgentDialog = ({ lead, open, onClose, onDone }) => {
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) api.get('/bdc/agents-workload').then(({ data }) => setAgents(data)).catch(() => {});
  }, [open]);
  const assign = async (a) => {
    setSaving(true);
    try {
      await api.post(`/bdc-leads/${lead.id}/assign-agent`, { agent_id: a.user_id });
      toast({ title: 'Lead atribuído', description: `${leadName(lead)} → ${a.name}` });
      onClose(); onDone && onDone();
    } catch (e) {
      toast({ title: 'Erro ao atribuir', description: e.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Assign BDC Agent</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Lead: <span className="text-foreground font-semibold">{lead && leadName(lead)}</span></p>
        <div className="space-y-2 max-h-[380px] overflow-y-auto py-1">
          {agents.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-8">Nenhum BDC Agent cadastrado. Crie usuários com o cargo "BDC Agent" em Administração.</p>}
          {agents.map((a) => (
            <button key={a.user_id} data-testid={`assign-agent-${a.user_id}`} disabled={saving} onClick={() => assign(a)} className="w-full flex items-center gap-3 p-3.5 rounded-lg border border-border hover:border-primary/60 hover:bg-primary/[0.06] transition-colors text-left disabled:opacity-50">
              <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(a.name)}&background=2563eb&color=fff`} alt="" className="w-10 h-10 rounded-full" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{a.name}</p>
                <p className="text-[11px] text-muted-foreground">{a.active_leads} ativos · {a.new_leads} novos · {a.follow_ups} follow-ups · {a.appointments_today} appts hoje</p>
              </div>
              <UserPlus className="w-4 h-4 text-primary" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AssignSalespersonDialog = ({ lead, open, onClose, onDone }) => {
  const [sps, setSps] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) api.get('/bdc/salespeople-workload').then(({ data }) => setSps(data)).catch(() => {});
  }, [open]);
  const assign = async (s) => {
    setSaving(true);
    try {
      await api.post(`/bdc-leads/${lead.id}/assign-salesperson`, { salesperson_id: s.user_id });
      toast({ title: 'Vendedor atribuído', description: `${leadName(lead)} → ${s.name} (CRM + Agenda)` });
      onClose(); onDone && onDone();
    } catch (e) {
      toast({ title: 'Erro ao atribuir', description: e.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-lg">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">Assign Salesperson</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">Cliente: <span className="text-foreground font-semibold">{lead && leadName(lead)}</span>{lead?.appointment_date ? ` · Appt: ${lead.appointment_date} ${lead.appointment_time}` : ''}</p>
        <div className="space-y-2 max-h-[380px] overflow-y-auto py-1">
          {sps.length === 0 && <p className="text-sm text-muted-foreground/70 text-center py-8">Nenhum vendedor cadastrado. Crie usuários com o cargo "Salesperson".</p>}
          {sps.map((s) => (
            <button key={s.user_id} data-testid={`assign-sp-${s.user_id}`} disabled={saving} onClick={() => assign(s)} className="w-full flex items-center gap-3 p-3.5 rounded-lg border border-border hover:border-emerald-400/50 hover:bg-emerald-400/[0.05] transition-colors text-left disabled:opacity-50">
              <img src={`https://ui-avatars.com/api/?name=${encodeURIComponent(s.name)}&background=059669&color=fff`} alt="" className="w-10 h-10 rounded-full" />
              <div className="flex-1">
                <p className="font-semibold text-sm">{s.name} <span className="text-[10px] text-muted-foreground/70 font-normal">({s.role})</span></p>
                <p className="text-[11px] text-muted-foreground">{s.crm_active} leads CRM · {s.bdc_assigned} do BDC · {s.appointments_today} appts hoje</p>
              </div>
              <Handshake className="w-4 h-4 text-emerald-400" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value }) => (
  <div><p className="text-[10px] tracking-wider text-muted-foreground/70 font-semibold uppercase">{label}</p><p className="text-sm mt-0.5">{value || '—'}</p></div>
);

export const CALL_OUTCOMES = ['No Answer', 'Voicemail', 'Wrong Number', 'Spoke With Customer', 'Customer Asked to Call Later', 'Not Interested', 'Follow-Up Needed'];
export const LOST_REASONS = ['Not Interested', 'Bought Elsewhere', 'Bad Credit / Financing', 'No Response', 'Wrong Number', 'Duplicate', 'Vehicle Unavailable', 'Price', 'Distance', 'Other'];

const ActionModal = ({ kind, lead, open, onClose, onDone }) => {
  const [f, setF] = useState({ outcome: 'No Answer', note: '', date: '', time: '', reason: 'Visita', detail: '', lost_reason: 'Not Interested' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ outcome: 'No Answer', note: '', date: '', time: '', reason: 'Visita', detail: '', lost_reason: 'Not Interested' }); }, [open, kind]);

  const titles = { call: 'Call Outcome', note: 'Add Note', followup: 'Follow-Up', appointment: 'Schedule Appointment', sendsp: 'Send to Salesperson', lost: 'Mark Lost' };

  const submit = async () => {
    if (!f.note.trim()) { toast({ title: 'Notes são obrigatórias' }); return; }
    if ((kind === 'followup' || kind === 'appointment') && (!f.date || !f.time)) { toast({ title: 'Informe data e hora' }); return; }
    if (kind === 'lost' && f.lost_reason === 'Other' && !f.detail.trim()) { toast({ title: 'Explique o motivo (Other)' }); return; }
    setSaving(true);
    try {
      if (kind === 'call') await api.post(`/bdc-leads/${lead.id}/call-outcome`, { outcome: f.outcome, note: f.note });
      if (kind === 'note') await api.post(`/bdc-leads/${lead.id}/note`, { note: f.note });
      if (kind === 'followup') await api.post(`/bdc-leads/${lead.id}/follow-up`, { date: f.date, time: f.time, note: f.note });
      if (kind === 'appointment') await api.post(`/bdc-leads/${lead.id}/appointment`, { date: f.date, time: f.time, reason: f.reason, note: f.note });
      if (kind === 'sendsp') await api.post(`/bdc-leads/${lead.id}/request-salesperson`, { note: f.note });
      if (kind === 'lost') await api.post(`/bdc-leads/${lead.id}/mark-lost`, { reason: f.lost_reason, detail: f.detail, note: f.note });
      toast({ title: `${titles[kind]} registrado` });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md">
        <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">{titles[kind]}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-1">
          {kind === 'call' && (
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Call Outcome *</label>
              <select data-testid="am-outcome" className={inputCls} value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })}>
                {CALL_OUTCOMES.map((o) => <option key={o} className="bg-popover">{o}</option>)}
              </select>
            </div>
          )}
          {kind === 'lost' && (
            <>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Lost Reason *</label>
                <select data-testid="am-lost-reason" className={inputCls} value={f.lost_reason} onChange={(e) => setF({ ...f, lost_reason: e.target.value })}>
                  {LOST_REASONS.map((o) => <option key={o} className="bg-popover">{o}</option>)}
                </select>
              </div>
              {f.lost_reason === 'Other' && <div><label className="text-xs text-muted-foreground mb-1.5 block">Explicação *</label><input data-testid="am-lost-detail" className={inputCls} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} /></div>}
            </>
          )}
          {(kind === 'followup' || kind === 'appointment') && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Data *</label><input data-testid="am-date" type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground mb-1.5 block">Hora *</label><input data-testid="am-time" type="time" className={inputCls} value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
            </div>
          )}
          {kind === 'appointment' && (
            <div><label className="text-xs text-muted-foreground mb-1.5 block">Motivo</label>
              <select data-testid="am-reason" className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
                {['Visita', 'Test-drive', 'Avaliação Trade-In', 'Financiamento', 'Retorno'].map((o) => <option key={o} className="bg-popover">{o}</option>)}
              </select>
            </div>
          )}
          <div><label className="text-xs text-muted-foreground mb-1.5 block">Notes * (obrigatório)</label><textarea data-testid="am-note" rows={3} className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Descreva o que aconteceu..." /></div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
          <button data-testid="am-save" onClick={submit} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const LeadDetailDialog = ({ leadId, open, onClose, canAssignAgent, canAssignSalesperson, onChanged, currentUserId }) => {
  const [data, setData] = useState(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const [spOpen, setSpOpen] = useState(false);
  const [action, setAction] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    try { const { data: d } = await api.get(`/bdc-leads/${leadId}`); setData(d); } catch (e) { setData(null); }
  };
  useEffect(() => { if (open && leadId) { setData(null); load(); } }, [open, leadId]);

  const l = data?.lead;
  const canWork = !!l && ['BDC_ASSIGNED', 'CONTACTING', 'FOLLOW_UP'].includes(l.status) && (canAssignAgent || l.bdc_agent_id === currentUserId);
  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-3xl max-h-[88vh] overflow-y-auto">
          {!l ? (
            <>
              <DialogHeader><DialogTitle className="sr-only">Carregando lead</DialogTitle></DialogHeader>
              <p className="text-muted-foreground text-sm py-8 text-center">Carregando...</p>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl uppercase flex items-center gap-3" data-testid="lead-detail-title">
                  {leadName(l)} <StatusBadge status={l.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 flex-wrap -mt-1">
                <span className="text-[11px] px-2 py-1 rounded bg-muted/50 border border-border text-foreground/65">Next Action: <span className="text-foreground font-semibold">{l.next_action}</span></span>
                <span className="text-[11px] text-muted-foreground/70">Recebido: {fmtDT(l.received_at)}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="rounded-lg border border-border/70 p-4 space-y-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-black tracking-widest text-primary uppercase flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5" /> Customer Information</p>
                    <button data-testid="edit-lead-customer-btn" onClick={() => setEditOpen(true)}
                      className="flex items-center gap-1.5 text-[11px] font-semibold border border-border rounded px-2 py-1 text-foreground/70 hover:text-foreground hover:border-primary/50 transition-colors">
                      <Pencil className="w-3 h-3" /> Editar
                    </button>
                  </div>
                  <Info label="Name" value={leadName(l)} />
                  <Info label="Phone" value={l.phone && <a href={`tel:${l.phone}`} className="text-primary hover:underline">{l.phone}</a>} />
                  <Info label="Email" value={l.email} />
                  <Info label="Preferred Contact" value={l.preferred_contact} />
                </div>
                <div className="rounded-lg border border-border/70 p-4 space-y-2.5">
                  <p className="text-[11px] font-black tracking-widest text-primary uppercase flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Lead Information</p>
                  <Info label="Source" value={<span className="inline-flex items-center gap-1.5"><SourceIcon source={l.source} />{`${l.source}${l.platform && l.platform !== l.source ? ` (${l.platform})` : ''}`}</span>} />
                  {l.external_lead_id && <Info label="External Lead ID" value={l.external_lead_id} />}
                  <Info label="Campaign / Ad" value={[l.campaign_name, l.adset_name, l.ad_name].filter(Boolean).join(' · ')} />
                  <Info label="Received" value={fmtDT(l.received_at)} />
                  {l.interested_vehicle && (l.interested_vehicle.year || l.interested_vehicle.vin) && (
                    <Info label="Interested Vehicle" value={`${[l.interested_vehicle.year, l.interested_vehicle.make, l.interested_vehicle.model, l.interested_vehicle.trim].filter(Boolean).join(' ')}${l.interested_vehicle.vin ? ` · VIN ${l.interested_vehicle.vin}` : ''}${l.interested_vehicle.matched ? ' · ✓ no estoque' : ' · fora do estoque'}`} />
                  )}
                  {l.response_time_seconds != null && <Info label="Response Time" value={`${Math.max(1, Math.round(l.response_time_seconds / 60))} min (horário comercial)`} />}
                  <Info label="Message" value={l.message} />
                </div>
                <div className="rounded-lg border border-border/70 p-4 space-y-2.5">
                  <p className="text-[11px] font-black tracking-widest text-primary uppercase flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Vehicle</p>
                  {data.vehicle ? (
                    <>
                      <Info label="Vehicle" value={`${data.vehicle.ano} ${data.vehicle.marca} ${data.vehicle.modelo} ${data.vehicle.trim || ''}`} />
                      <Info label="Stock # / VIN" value={`${data.vehicle.placa} · ${data.vehicle.vin || '—'}`} />
                      <Info label="Price" value={`$${Number(data.vehicle.preco).toLocaleString('en-US')}`} />
                    </>
                  ) : <Info label="Vehicle of Interest" value={l.vehicle_interest} />}
                </div>
                <div className="rounded-lg border border-border/70 p-4 space-y-2.5">
                  <p className="text-[11px] font-black tracking-widest text-primary uppercase">Responsible</p>
                  <Info label="BDC Agent" value={l.bdc_agent_name} />
                  <Info label="BDC Manager" value={l.bdc_manager_name} />
                  <Info label="Salesperson" value={l.salesperson_name} />
                  {l.lost_reason && <Info label="Lost Reason" value={`${l.lost_reason}${l.lost_detail ? ` — ${l.lost_detail}` : ''}`} />}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {l.phone && <a data-testid="call-customer-btn" href={`tel:${l.phone}`} onClick={() => setTimeout(() => setAction('call'), 400)} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors"><Phone className="w-4 h-4" /> CALL CUSTOMER</a>}
                {canWork && (
                  <>
                    <button data-testid="log-call-btn" onClick={() => setAction('call')} className="flex items-center gap-2 border border-border hover:border-emerald-400/50 text-foreground/85 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">Log Call</button>
                    <button data-testid="add-note-btn" onClick={() => setAction('note')} className="flex items-center gap-2 border border-border hover:border-foreground/30 text-foreground/85 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">Add Note</button>
                    <button data-testid="schedule-appt-btn" onClick={() => setAction('appointment')} className="flex items-center gap-2 border border-border hover:border-violet-400/50 text-foreground/85 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">Schedule Appointment</button>
                    <button data-testid="send-sp-btn" onClick={() => setAction('sendsp')} className="flex items-center gap-2 border border-border hover:border-orange-400/50 text-foreground/85 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">Send to Salesperson</button>
                    <button data-testid="mark-lost-btn" onClick={() => setAction('lost')} className="flex items-center gap-2 border border-border hover:border-red-400/50 text-foreground/65 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors">Mark Lost</button>
                  </>
                )}
                {canAssignAgent && ['NEW', 'BDC_ASSIGNED', 'CONTACTING', 'FOLLOW_UP'].includes(l.status) && (
                  <button data-testid="open-assign-agent" onClick={() => setAgentOpen(true)} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"><UserPlus className="w-4 h-4" /> {l.bdc_agent_name ? 'Reatribuir Agent' : 'Assign BDC Agent'}</button>
                )}
                {canAssignSalesperson && ['AWAITING_SALES_ASSIGNMENT', 'SALESPERSON_REQUESTED'].includes(l.status) && (
                  <button data-testid="open-assign-sp" onClick={() => setSpOpen(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"><Handshake className="w-4 h-4" /> Assign Salesperson</button>
                )}
                {canAssignSalesperson && isWalkIn(l) && ['NEW', 'BDC_ASSIGNED', 'CONTACTING', 'FOLLOW_UP'].includes(l.status) && (
                  <button data-testid="direct-assign-sp" onClick={() => setSpOpen(true)} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors"><Handshake className="w-4 h-4" /> Enviar Direto ao Vendedor (Walk-In)</button>
                )}
              </div>
              <div className="rounded-lg border border-border/70 p-4">
                <p className="text-[11px] font-black tracking-widest text-primary uppercase mb-3">Activity Timeline</p>
                <div className="space-y-0 max-h-[280px] overflow-y-auto" data-testid="activity-timeline">
                  {(data.activities || []).map((a) => (
                    <div key={a.id} className="flex gap-3 relative pb-4 last:pb-0">
                      <div className="flex flex-col items-center">
                        <span className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                        <span className="w-px flex-1 bg-muted/50" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm"><span className="font-semibold">{a.action}</span></p>
                        <p className="text-[11px] text-muted-foreground">{fmtDT(a.timestamp)} · {a.user_name}{a.user_role && a.user_role !== 'System' ? ` (${a.user_role})` : ''}{a.old_value ? ` · de: ${a.old_value}` : ''}</p>
                        {a.note && <p className="text-[12px] text-foreground/65 mt-1 bg-muted/40 border border-border/70 rounded px-2.5 py-1.5">{a.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <EditLeadCustomerDialog lead={l} open={editOpen} onClose={() => setEditOpen(false)} onDone={() => { load(); onChanged && onChanged(); }} />
      <AssignAgentDialog lead={l} open={agentOpen} onClose={() => setAgentOpen(false)} onDone={() => { load(); onChanged && onChanged(); }} />
      <AssignSalespersonDialog lead={l} open={spOpen} onClose={() => setSpOpen(false)} onDone={() => { load(); onChanged && onChanged(); }} />
      <ActionModal kind={action || 'note'} lead={l || {}} open={!!action} onClose={() => setAction(null)} onDone={() => { load(); onChanged && onChanged(); }} />
    </>
  );
};
